/**
 * Kaleidoscope / mandala: N-fold rotational symmetry + mirror within each wedge,
 * sampled from a center square crop. Output is a square disk on white (classic coloring-page base).
 */

function sampleBilinear(data, width, height, x, y) {
  const cx = Math.max(0, Math.min(width - 1, x))
  const cy = Math.max(0, Math.min(height - 1, y))
  const x0 = Math.floor(cx)
  const y0 = Math.floor(cy)
  const x1 = Math.min(x0 + 1, width - 1)
  const y1 = Math.min(y0 + 1, height - 1)
  const fx = cx - x0
  const fy = cy - y0
  const i00 = (y0 * width + x0) * 4
  const i10 = (y0 * width + x1) * 4
  const i01 = (y1 * width + x0) * 4
  const i11 = (y1 * width + x1) * 4
  const lerp = (a, b, t) => a + (b - a) * t
  const r = lerp(lerp(data[i00], data[i10], fx), lerp(data[i01], data[i11], fx), fy)
  const g = lerp(lerp(data[i00 + 1], data[i10 + 1], fx), lerp(data[i01 + 1], data[i11 + 1], fx), fy)
  const b = lerp(lerp(data[i00 + 2], data[i10 + 2], fx), lerp(data[i01 + 2], data[i11 + 2], fx), fy)
  return { r: r | 0, g: g | 0, b: b | 0 }
}

/**
 * @param {HTMLCanvasElement} src
 * @param {object} [options]
 * @param {number} [options.segments=12] — symmetry wedges (6–24 typical for mandalas)
 * @param {boolean} [options.mirror=true] — mirror within wedge (full kaleidoscope)
 * @param {number} [options.outSize] — output square side (default: min square from src)
 * @returns {HTMLCanvasElement}
 */
export function photoToMandalaCanvas(src, options = {}) {
  const segments = Math.max(3, Math.min(32, Math.round(options.segments ?? 12)))
  const mirror = options.mirror !== false

  const sw = src.width
  const sh = src.height
  const side = Math.min(sw, sh)
  const sx0 = Math.floor((sw - side) / 2)
  const sy0 = Math.floor((sh - side) / 2)

  const square = document.createElement('canvas')
  square.width = side
  square.height = side
  square.getContext('2d', { willReadFrequently: true }).drawImage(
    src,
    sx0,
    sy0,
    side,
    side,
    0,
    0,
    side,
    side
  )

  const outSize = Math.max(
    64,
    Math.min(4096, Math.round(options.outSize ?? side))
  )
  const out = document.createElement('canvas')
  out.width = outSize
  out.height = outSize
  const ctx = out.getContext('2d', { willReadFrequently: true })
  const imgData = ctx.createImageData(outSize, outSize)
  const dst = imgData.data

  const srcCtx = square.getContext('2d', { willReadFrequently: true })
  const srcImage = srcCtx.getImageData(0, 0, side, side)
  const srcData = srcImage.data

  const cx = outSize / 2
  const cy = outSize / 2
  const srcCx = side / 2
  const srcCy = side / 2
  const maxROut = Math.min(cx, cy) - 0.5
  const srcMaxR = Math.min(srcCx, srcCy) - 0.5
  const sector = (2 * Math.PI) / segments
  const half = sector / 2
  const twoPi = 2 * Math.PI

  for (let y = 0; y < outSize; y++) {
    const dy = y - cy + 0.5
    for (let x = 0; x < outSize; x++) {
      const dx = x - cx + 0.5
      const r = Math.sqrt(dx * dx + dy * dy)
      const di = (y * outSize + x) * 4

      if (r > maxROut) {
        dst[di] = 255
        dst[di + 1] = 255
        dst[di + 2] = 255
        dst[di + 3] = 255
        continue
      }

      let theta = Math.atan2(dy, dx)
      let t = theta + Math.PI
      if (t < 0) t += twoPi
      if (t >= twoPi) t -= twoPi

      let tLocal = t % sector
      if (mirror) {
        if (tLocal > half) tLocal = sector - tLocal
      }

      const alpha = tLocal - half
      const sr = (r / maxROut) * srcMaxR
      const sxp = srcCx + sr * Math.cos(alpha)
      const syp = srcCy + sr * Math.sin(alpha)
      const { r: pr, g: pg, b: pb } = sampleBilinear(srcData, side, side, sxp, syp)
      dst[di] = pr
      dst[di + 1] = pg
      dst[di + 2] = pb
      dst[di + 3] = 255
    }
  }

  ctx.putImageData(imgData, 0, 0)
  return out
}
